/*
 * Ajax cart drawer — IG redesign layer.
 *
 * Adds the per-line "Regalo" toggle. The flag is stored the way the live store
 * stores it: a hidden line item property named `_gift`, so the existing back
 * office keeps reading the same field.
 *
 * Two Shopify quirks shape this file:
 *
 * 1. `/cart/change.js` replaces a line's property object wholesale rather than
 *    merging into it, so the current properties travel in `data-properties` and
 *    are re-sent in full on every toggle.
 * 2. An empty `properties` object is ignored rather than clearing the line, and
 *    an empty string leaves the key behind with a blank value. Clearing the
 *    last property therefore means dropping the line and adding it back clean —
 *    the same thing the live store does.
 *
 * Toggling also changes the line item key (Shopify derives it from the variant
 * plus the properties) and can merge two lines into one, so the drawer sections
 * are re-rendered from the response instead of being patched in place.
 *
 * Loaded from `snippets/cart-drawer.liquid` when
 * `settings.use_ajax_cart_redesign` is on.
 */

if (!customElements.get("cart-item-gift")) {
  customElements.define(
    "cart-item-gift",
    class CartItemGift extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector(".cart-item__gift-input");
        if (!this.input) return;

        this.onChange = this.onChange.bind(this);
        this.input.addEventListener("change", this.onChange);
      }

      disconnectedCallback() {
        this.input?.removeEventListener("change", this.onChange);
      }

      get cartItems() {
        return this.closest("cart-drawer-items");
      }

      get line() {
        return this.closest(".cart-item");
      }

      /**
       * The line's current properties, as rendered by Liquid. Anything already
       * on the line (`_comparedAtPrice`, engraving text, …) must survive the
       * round trip, so it is sent back untouched alongside the gift flag.
       *
       * Liquid's `json` filter serialises an empty property hash as `[]`, and
       * a key set on an array is dropped by `JSON.stringify` — which silently
       * loses the gift flag. Anything that is not a plain object is discarded.
       */
      get properties() {
        try {
          const parsed = JSON.parse(this.dataset.properties || "{}");
          const isPlainObject =
            parsed !== null &&
            typeof parsed === "object" &&
            !Array.isArray(parsed);

          return isPlainObject ? parsed : {};
        } catch (error) {
          console.error(error);
          return {};
        }
      }

      async onChange() {
        const cartItems = this.cartItems;
        if (!cartItems) return;

        const checked = this.input.checked;
        const properties = this.properties;

        if (checked) {
          properties._gift = true;
        } else {
          delete properties._gift;
        }

        const sections = cartItems.getSectionsToRender();
        const errors =
          document.getElementById("CartDrawer-CartErrors") ||
          document.getElementById("cart-errors");
        const totalBefore = this.cartTotal;

        this.classList.add("is-loading");
        if (errors) errors.textContent = "";

        try {
          const clearsEveryProperty =
            !checked && Object.keys(properties).length === 0;

          const state = clearsEveryProperty
            ? await this.rebuildLine(sections)
            : await this.request(routes.cart_change_url, {
                id: this.dataset.key,
                properties,
                sections: sections.map((section) => section.section),
                sections_url: window.location.pathname,
              });

          this.render(cartItems, sections, state, totalBefore);

          const drawer = document.querySelector("cart-drawer");
          if (drawer) drawer.classList.toggle("is-empty", state.item_count === 0);

          document.dispatchEvent(
            new CustomEvent("cart:change", {
              detail: { cart: state, sectionId: cartItems.dataset.source },
            })
          );

          publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
        } catch (error) {
          console.error(error);
          this.classList.remove("is-loading");
          this.input.checked = !checked;
          if (errors) errors.textContent = window.cartStrings.error;
        }
      }

      /**
       * The cart total in cents, as the shipping bar last rendered it.
       * `null` when the bar is not on the page, which disables the comparison
       * and falls back to a full re-render.
       */
      get cartTotal() {
        const total = document.querySelector(".cart-shipping")?.dataset.total;
        return total === undefined ? null : Number(total);
      }

      /**
       * A gift flag carries no price, so the totals cannot move — only the list
       * of lines can, when a toggle merges two of them. Re-rendering the whole
       * drawer would rebuild the footer as well, and the free-shipping bar
       * animates its width from `--progress-prev` every time it is rebuilt: the
       * bar would visibly re-fill on a change that never touched the total.
       *
       * So the line list alone is replaced, and the footer — bar, subtotal and
       * whatever the shopper has already typed into the note — is left alone.
       * If the total did move after all, the full re-render still runs.
       */
      render(cartItems, sections, state, totalBefore) {
        const drawerSection = sections.find(
          (section) => section.selector === ".drawer__inner"
        );
        const totalHeld =
          totalBefore !== null && state.total_price === totalBefore;

        sections.forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container) return;

          const isDrawer = section === drawerSection;
          const selector =
            isDrawer && totalHeld ? "#CartDrawer-CartItems" : section.selector;
          const target = container.querySelector(selector) || container;

          target.innerHTML = cartItems.getSectionInnerHTML(
            state.sections[section.section],
            selector
          );
        });

        if (totalHeld) return;

        // The freshly rendered shipping bar has an up-to-date `data-total` but
        // an empty message / zero-width fill until this recomputes it.
        if (document.querySelector(".cart-shipping")) {
          const refreshed =
            document.querySelector("cart-drawer-items") || cartItems;
          refreshed.cartShipping?.();
        }
      }

      /**
       * Drops the line and adds the variant back with no properties at all.
       *
       * The order matters: the quantity is freed before it is requested again,
       * so a variant with a single unit left — most of this catalogue — does not
       * fail the add against its own line.
       *
       * `/cart/add.js` answers with the added item rather than the cart, so the
       * rendered sections and the totals are asked for separately.
       */
      async rebuildLine(sections) {
        const line = this.line;
        const variantId = line?.dataset.variantId;
        const quantity = parseInt(line?.dataset.quantity || "1", 10);

        if (!variantId) throw new Error("cart-item-gift: line item not found");

        await this.request(routes.cart_change_url, {
          id: this.dataset.key,
          quantity: 0,
        });

        await this.request(routes.cart_add_url, {
          items: [{ id: variantId, quantity }],
        });

        return this.request(routes.cart_update_url, {
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        });
      }

      async request(url, payload) {
        const response = await fetch(url, {
          ...fetchConfig(),
          ...{ body: JSON.stringify(payload) },
        });
        const state = await response.json();

        if (state.status || state.errors) {
          throw new Error(state.description || state.message || state.errors);
        }

        return state;
      }
    }
  );
}
